// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {APP_ZOOM_LEVEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {ActiveNowTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/ActiveNowTab';
import {
	HdrTabContent,
	shouldShowHdrSettings,
} from '@app/features/user/components/modals/tabs/appearance_tab/AppearanceTabHdrTab';
import {ChannelListTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/ChannelListTab';
import {InterfaceTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/InterfaceTab';
import {
	AppearanceTabPreview,
	MessagesTabContent,
} from '@app/features/user/components/modals/tabs/appearance_tab/MessagesTab';
import {
	AppZoomLevelTabContent,
	FontSizeTabContent,
	useAppZoomLevelDescription,
} from '@app/features/user/components/modals/tabs/appearance_tab/ScalingTab';
import {StreamerModeTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/StreamerModeTab';
import {ThemeTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/theme/ThemeTabContent';
import {shouldShowAppZoomLevel} from '@app/features/user/components/settings_utils/AppZoomLevelUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const THEME_DESCRIPTOR = msg({
	message: 'Theme',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CHOOSE_A_BASE_APPEARANCE_MANAGE_QUICK_CSS_ORGANIZE_DESCRIPTOR = msg({
	message: 'Choose a base appearance, manage quick CSS, organize saved theme files, and tune theme tokens.',
	comment: 'Button or menu action label in the appearance tab. Keep it concise. Keep the tone plain and specific.',
});
const HIGH_DYNAMIC_RANGE_DESCRIPTOR = msg({
	message: 'High dynamic range',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CONTROL_HOW_HDR_IMAGES_ARE_DISPLAYED_ON_HDR_DESCRIPTOR = msg({
	message: 'Control how HDR images are displayed on HDR-capable monitors.',
	comment: 'Description text in the appearance tab.',
});
const CHAT_FONT_SCALING_DESCRIPTOR = msg({
	message: 'Chat font scaling',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const ADJUST_THE_FONT_SIZE_IN_THE_CHAT_AREA_DESCRIPTOR = msg({
	message: 'Adjust the font size in the chat area.',
	comment: 'Description text in the appearance tab.',
});
const MESSAGES_DESCRIPTOR = msg({
	message: 'Messages',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CHOOSE_HOW_MESSAGES_ARE_DISPLAYED_IN_CHAT_CHANNELS_DESCRIPTOR = msg({
	message: 'Choose how messages are displayed in chat channels.',
	comment: 'Button or menu action label in the appearance tab. Keep it concise.',
});
const INTERFACE_DESCRIPTOR = msg({
	message: 'Interface',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CUSTOMIZE_INTERFACE_ELEMENTS_AND_BEHAVIORS_DESCRIPTOR = msg({
	message: 'Customize interface elements and behaviors.',
	comment: 'Description text in the appearance tab.',
});
const CHANNEL_LIST_DESCRIPTOR = msg({
	message: 'Channel list',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CONTROL_UNREAD_INDICATOR_BEHAVIOR_FOR_MUTED_CHANNELS_IN_DESCRIPTOR = msg({
	message: 'Control unread indicator behavior for muted channels in channel lists.',
	comment: 'Description text in the appearance tab.',
});
const ACTIVE_NOW_DESCRIPTOR = msg({
	message: 'Active now',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CONTROL_HOW_ACTIVE_NOW_SURFACES_ACROSS_THE_APP_DESCRIPTOR = msg({
	message: 'Control how active now surfaces across the app.',
	comment: 'Description text in the appearance tab.',
});
const STREAMER_MODE_DESCRIPTOR = msg({
	message: 'Streaming privacy',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const HIDE_PERSONAL_INFO_WHILE_STREAMING_DESCRIPTOR = msg({
	message: 'Mask names, private details, invites, sounds, and alerts while sharing.',
	comment: 'Description text in the appearance tab.',
});
export const AppearanceTab: React.FC = observer(() => {
	const {i18n} = useLingui();
	const showZoomLevel = shouldShowAppZoomLevel();
	const showHdrSettings = shouldShowHdrSettings();
	const appZoomLevelDescription = useAppZoomLevelDescription();
	return (
		<SettingsTabContainer data-flx="user.appearance-tab.settings-tab-container">
			{!MobileLayout.enabled && <AppearanceTabPreview data-flx="user.appearance-tab.appearance-tab-preview" />}
			<SettingsTabContent data-flx="user.appearance-tab.settings-tab-content">
				<SettingsSection
					id="theme"
					title={i18n._(THEME_DESCRIPTOR)}
					description={i18n._(CHOOSE_A_BASE_APPEARANCE_MANAGE_QUICK_CSS_ORGANIZE_DESCRIPTOR)}
					data-flx="user.appearance-tab.theme"
				>
					<ThemeTabContent data-flx="user.appearance-tab.theme-tab-content" />
				</SettingsSection>
				{showHdrSettings ? (
					<SettingsSection
						id="hdr"
						title={i18n._(HIGH_DYNAMIC_RANGE_DESCRIPTOR)}
						description={i18n._(CONTROL_HOW_HDR_IMAGES_ARE_DISPLAYED_ON_HDR_DESCRIPTOR)}
						data-flx="user.appearance-tab.hdr"
					>
						<HdrTabContent data-flx="user.appearance-tab.hdr-tab-content" />
					</SettingsSection>
				) : null}
				<SettingsSection
					id="chat-font-scaling"
					title={i18n._(CHAT_FONT_SCALING_DESCRIPTOR)}
					description={i18n._(ADJUST_THE_FONT_SIZE_IN_THE_CHAT_AREA_DESCRIPTOR)}
					data-flx="user.appearance-tab.chat-font-scaling"
				>
					<FontSizeTabContent data-flx="user.appearance-tab.font-size-tab-content" />
				</SettingsSection>
				{showZoomLevel ? (
					<SettingsSection
						id="app-zoom-level"
						title={i18n._(APP_ZOOM_LEVEL_DESCRIPTOR)}
						description={appZoomLevelDescription}
						data-flx="user.appearance-tab.app-zoom-level"
					>
						<AppZoomLevelTabContent data-flx="user.appearance-tab.app-zoom-level-tab-content" />
					</SettingsSection>
				) : null}
				<SettingsSection
					id="streamer-mode"
					title={i18n._(STREAMER_MODE_DESCRIPTOR)}
					description={i18n._(HIDE_PERSONAL_INFO_WHILE_STREAMING_DESCRIPTOR)}
					data-flx="user.appearance-tab.streamer-mode"
				>
					<StreamerModeTabContent />
				</SettingsSection>
				<SettingsSection
					id="messages"
					title={i18n._(MESSAGES_DESCRIPTOR)}
					description={i18n._(CHOOSE_HOW_MESSAGES_ARE_DISPLAYED_IN_CHAT_CHANNELS_DESCRIPTOR)}
					data-flx="user.appearance-tab.messages"
				>
					<MessagesTabContent data-flx="user.appearance-tab.messages-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="interface"
					title={i18n._(INTERFACE_DESCRIPTOR)}
					description={i18n._(CUSTOMIZE_INTERFACE_ELEMENTS_AND_BEHAVIORS_DESCRIPTOR)}
					data-flx="user.appearance-tab.interface"
				>
					<InterfaceTabContent data-flx="user.appearance-tab.interface-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="channel-list"
					title={i18n._(CHANNEL_LIST_DESCRIPTOR)}
					description={i18n._(CONTROL_UNREAD_INDICATOR_BEHAVIOR_FOR_MUTED_CHANNELS_IN_DESCRIPTOR)}
					data-flx="user.appearance-tab.channel-list"
				>
					<ChannelListTabContent data-flx="user.appearance-tab.channel-list-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="active-now"
					tabType="appearance"
					title={i18n._(ACTIVE_NOW_DESCRIPTOR)}
					description={i18n._(CONTROL_HOW_ACTIVE_NOW_SURFACES_ACROSS_THE_APP_DESCRIPTOR)}
					data-flx="user.appearance-tab.active-now"
				>
					<ActiveNowTabContent data-flx="user.appearance-tab.active-now-tab-content" />
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});
